import { FC } from 'react';
import {
  DashboardContainer, DashboardContainerBody, DashboardContainerHeader, JustifiedRow, ResolvedText, ScrollableList,
  SideDrawerLink, StyledTitle
} from '../MoaDashboard.styles';
import { ExtractionsContainerProps } from './ExtractionsContainer.types';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import { ExtractJobStatus } from '../../../services/dashboard/dashboard.types';
import { ITooltipParams } from 'ag-grid-community';
import FailedAlert from "../FailedAlert";
import EventProgressBar from "../EventProgressBar";
import ExtractMetricsDisplay from "../ExtractMetricsDisplay";

const ExtractionsContainer: FC<ExtractionsContainerProps> = (
    {
      inProgress,
      upNext,
      done,
      setDrawerColumnDefs,
      setOpenDrawer,
      setDrawerType,
      setFetchExtractionsFailure,
      setExtractionAndActivationsJobId,
      setRequestUserName
    }
) => {
  const muiTheme = createTheme();

  const extractionsColumnDefs = [
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'label',
      headerName: 'Store#',
      sortable: true,
      floatingFilter: false
    },
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'marketNo',
      headerName: 'FZ Code',
      sortable: false,
      floatingFilter: false
    },
    {
      minWidth: 400,
      field: 'reason',
      headerName: 'Error Reason',
      sortable: false,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'isResolved',
      headerName: '',
      sortable: false,
      floatingFilter: false,
      cellRenderer: (params: any) => {
        if (params.value) {
          return <ResolvedText>Resolved</ResolvedText>;
        }
        else {
          return '';
        }
      }
    }
  ];

  const couponsColumnDefs = [
    {
      minWidth: 100,
      field: 'label',
      headerName: 'Coupon Group',
      sortable: true,
      floatingFilter: false
    },
    {
      minWidth: 400,
      field: 'reason',
      headerName: 'Error Reason',
      sortable: false,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    }
  ];

  const defineDrawer = (progressMetrics: ExtractJobStatus) => {
    switch (progressMetrics.extractType) {
      case 'COUPONS':
        setDrawerType('couponsExtractions');
        setDrawerColumnDefs(couponsColumnDefs);
        setExtractionAndActivationsJobId(progressMetrics.jobId);
        setFetchExtractionsFailure(true);
        setOpenDrawer(true);
        return;
      case 'EXTRACT':
        setDrawerType('extract');
        setDrawerColumnDefs(extractionsColumnDefs);
        setExtractionAndActivationsJobId(progressMetrics.jobId);
        setRequestUserName(progressMetrics.userName);
        setFetchExtractionsFailure(true);
        setOpenDrawer(true);
        return;
      case 'ACTIVATE':
        setDrawerType('activate');
        setDrawerColumnDefs(extractionsColumnDefs);
        setExtractionAndActivationsJobId(progressMetrics.jobId);
        setRequestUserName(progressMetrics.userName);
        setFetchExtractionsFailure(true);
        setOpenDrawer(true);
        return;
      default:
        return;
    }
  };

  const renderStatus = (
      progressMetrics: ExtractJobStatus
  ) => {
    return (
        <>
          {progressMetrics.failed !== 0 &&
            <>
              {progressMetrics.failed > progressMetrics.resolved
                  ? <FailedAlert
                      count={progressMetrics.failed - progressMetrics.resolved}
                      openDrawer={() => defineDrawer(progressMetrics)}
                      id={progressMetrics.jobId}
                  />
                  : <SideDrawerLink
                      dataTestId={'resolvedText-' + progressMetrics.jobId}
                      label={progressMetrics.resolved + ' resolved'}
                      to={''}
                      onClick={() => defineDrawer(progressMetrics)}
                  />
              }
            </>
          }
        </>
    )
  };

  const renderInProgress = () => {
    return (
        <ScrollableList data-testid="extractInProgressContainer" style={{ height: '160px' }}>
          {inProgress.length === 0
              ? <StyledTitle>No extractions in progress</StyledTitle>
              : inProgress.map((inProgressMetrics) =>
                  (
                      <JustifiedRow key={`extract-in-progress-${inProgressMetrics.jobId}`}>
                        <EventProgressBar
                            name={`${inProgressMetrics.extractType} for ${inProgressMetrics.userName}`}
                            processed={inProgressMetrics.processed}
                            total={inProgressMetrics.total}
                        />
                        {renderStatus(inProgressMetrics)}
                      </JustifiedRow>
                  )
              )
          }
        </ScrollableList>
    );
  };

  return (
      <DashboardContainer data-test={'extractionsContainer'} style={{ minHeight: '400px', maxHeight: '600px' }}>
        <DashboardContainerHeader data-testid="extractionsContainerHeader">Extractions</DashboardContainerHeader>
        <DashboardContainerBody>
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            {renderInProgress()}
            <Divider orientation="horizontal" variant={'fullWidth'} flexItem/>
            <ExtractMetricsDisplay
                extractMetrics={upNext}
                title={'Up next'}
                emptyText={'No extractions waiting'}
                openDrawer={defineDrawer}
                testName={'upNextExtract'}
            />
            <Divider orientation="horizontal" variant={'fullWidth'} flexItem/>
            <ExtractMetricsDisplay
                extractMetrics={done}
                title={'Done'}
                emptyText={'No extractions'}
                openDrawer={defineDrawer}
                testName={'doneExtract'}
            />
          </ThemeProvider>
        </DashboardContainerBody>
      </DashboardContainer>
  );
};

export default ExtractionsContainer;
